import { useEffect, useState, useCallback } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { InfoCard, Progress, TabbedCard, CardTab, EmptyState } from '@backstage/core-components';
import { Box, Typography } from '@material-ui/core';

const KUBERNETES_ANNOTATION = 'backstage.io/kubernetes-label-selector';
const COROOT_APP_NAME_ANNOTATION = 'coroot.com/application-name';

interface ProjectTab {
  projectId: string;
  projectName: string;
  appId: string;
  iframeUrl: string;
}

export const CorootContent = () => {
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  
  const backendBaseUrl =
    configApi.getOptionalString('backend.baseUrl') ?? 'http://localhost:7007';
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cookieSet, setCookieSet] = useState(false);
  const [tabs, setTabs] = useState<ProjectTab[]>([]);

  // Cookie renewal interval in milliseconds (e.g., every 5 minutes)
  const COOKIE_RENEWAL_INTERVAL = 5 * 60 * 1000;

  // Get label selector from entity annotation
  const labelSelector = entity.metadata.annotations?.[KUBERNETES_ANNOTATION];
  const corootAppName =
    entity.metadata.annotations?.[COROOT_APP_NAME_ANNOTATION];

  // Parse label selector into key-value pairs
  const parseLabelSelector = useCallback((selector: string): Record<string, string> => {
    const labels: Record<string, string> = {};
    const pairs = selector.split(',').map(s => s.trim());
    
    pairs.forEach(pair => {
      const [key, value] = pair.split('=').map(s => s.trim());
      if (key && value) {
        labels[key] = value;
      }
    });
    
    return labels;
  }, []);

  // Set up initial cookie fetch and periodic renewal
  useEffect(() => {
    const fetchCookie = async () => {
      try {
        const response = await fetchApi.fetch(
          `${backendBaseUrl}/api/coroot-proxy/cookie`,
        );

        if (response.ok) {
          setCookieSet(true);
        }
      } catch (err) {
        // Cookie fetch failed, will retry on next interval
      }
    };

    fetchCookie();
    const intervalId = setInterval(fetchCookie, COOKIE_RENEWAL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [backendBaseUrl, fetchApi, COOKIE_RENEWAL_INTERVAL]);

  // Fetch projects and find matching applications
  useEffect(() => {
    const fetchProjectsAndApps = async () => {
      if (!cookieSet) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Use explicit Coroot annotation if present, otherwise derive from k8s label selector
        let appName = corootAppName || entity.metadata.name;
        if (!corootAppName && labelSelector) {
          const labels = parseLabelSelector(labelSelector);
          appName = labels.app || labels['app.kubernetes.io/name'] || entity.metadata.name;
        }

        // Fetch user info to get accessible projects
        const userResponse = await fetchApi.fetch(
          `${backendBaseUrl}/api/coroot-proxy/api/user`,
        );

        if (!userResponse.ok) {
          throw new Error('Failed to fetch user projects');
        }

        const userData = await userResponse.json();
        const allProjects = userData.projects || [];
        
        // Filter out projects with "global" in their name
        const projects = allProjects.filter((project: any) => {
          const projectName = (project.name || project.id || '').toLowerCase();
          return !projectName.includes('global');
        });

        // Fetch all project applications in parallel for better performance
        const projectPromises = projects.map(async (project: any) => {
          try {
            const appsResponse = await fetchApi.fetch(
              `${backendBaseUrl}/api/coroot-proxy/api/project/${project.id}/overview/applications`,
            );

            if (!appsResponse.ok) {
              return null;
            }

            const appsData = await appsResponse.json();
            const applications = appsData.context?.search?.applications || [];

            // Search for application matching the app name
            // Coroot app IDs follow format: projectId:namespace:kind:name
            // e.g., "f1kne50r:tooling:Deployment:backstage"
            // We match against the last part (the app name)
            const matchingApp = applications.find((app: any) => {
              // Extract the app name from the ID (last part after final colon)
              const appIdParts = app.id?.split(':') || [];
              const appNameFromId = appIdParts[appIdParts.length - 1];
              
              // Match against the app name extracted from label selector
              return appNameFromId === appName;
            });

            if (matchingApp) {
              const encodedAppId = encodeURIComponent(matchingApp.id);
              return {
                projectId: project.id,
                projectName: project.name || project.id,
                appId: matchingApp.id,
                iframeUrl: `${backendBaseUrl}/api/coroot-proxy/p/${project.id}/app/${encodedAppId}`,
              };
            }

            return null;
          } catch (err) {
            // Failed to fetch apps for this project, continue with others
            return null;
          }
        });

        // Wait for all requests to complete in parallel
        const results = await Promise.all(projectPromises);
        const foundTabs = results.filter((tab): tab is ProjectTab => tab !== null);

        if (foundTabs.length === 0) {
          setError(`No matching applications found for: ${appName}`);
        }

        setTabs(foundTabs);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load Coroot data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectsAndApps();
  }, [labelSelector, corootAppName, cookieSet, backendBaseUrl, fetchApi, parseLabelSelector, entity.metadata.name]);

  // Show loading state
  if (isLoading || !cookieSet) {
    return (
      <InfoCard title="Coroot Application Details">
        <Box display="flex" justifyContent="center" p={3}>
          <Progress />
        </Box>
      </InfoCard>
    );
  }

  // Show error state
  if (error) {
    return (
      <InfoCard title="Coroot Application Details">
        <EmptyState
          missing="data"
          title="Failed to load Coroot data"
          description={error}
        />
      </InfoCard>
    );
  }

  // Show tabs with iframes
  if (tabs.length === 0) {
    return (
      <InfoCard title="Coroot Application Details">
        <EmptyState
          missing="data"
          title="No applications found"
          description={`No matching applications found for: ${entity.metadata.name}`}
        />
      </InfoCard>
    );
  }

  if (tabs.length === 1) {
    return (
      <InfoCard title="Coroot Application Details">
        <Box>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Project: {tabs[0].projectName} | Application: {tabs[0].appId.split(':').pop()}
          </Typography>
          <iframe
            title={`Coroot - ${tabs[0].projectName} - ${tabs[0].appId}`}
            src={tabs[0].iframeUrl}
            style={{
              width: '100%',
              height: '800px',
              border: 'none',
              borderRadius: '4px',
            }}
          />
        </Box>
      </InfoCard>
    );
  }

  return (
    <InfoCard title="Coroot Application Details">
      <TabbedCard>
        {tabs.map(tab => (
          <CardTab key={tab.projectId} label={tab.projectName}>
            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Project: {tab.projectName} | Application: {tab.appId.split(':').pop()}
              </Typography>
              <iframe
                title={`Coroot - ${tab.projectName} - ${tab.appId}`}
                src={tab.iframeUrl}
                style={{
                  width: '100%',
                  height: '800px',
                  border: 'none',
                  borderRadius: '4px',
                }}
              />
            </Box>
          </CardTab>
        ))}
      </TabbedCard>
    </InfoCard>
  );
};
